import api from './api';
import type { Department } from '../types';

// Define the DTOs that are not exported from types
interface CreateDepartmentDTO {
  name: string;
  description?: string;
}

interface UpdateDepartmentDTO {
  name?: string;
  description?: string;
}

class DepartmentService {
  // Get all departments
  async getDepartments(): Promise<{ departments: Department[], total: number }> {
    try {
      const response = await api.get('/departments');
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  }

  // Get departments for a specific hospital
  async getHospitalDepartments(hospitalId: string): Promise<{ departments: Department[], total: number }> {
    try {
      const response = await api.get(`/departments/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching departments for hospital ${hospitalId}:`, error);
      throw error;
    }
  }

  // Get department by ID
  async getDepartmentById(id: string): Promise<Department> {
    try {
      const response = await api.get(`/departments/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching department with id ${id}:`, error);
      throw error;
    }
  }

  // Create department
  async createDepartment(hospitalId: string, departmentData: CreateDepartmentDTO): Promise<Department> {
    try {
      const response = await api.post('/departments', {
        ...departmentData,
        hospitalId
      });
      return response.data;
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  }

  // Update department
  async updateDepartment(id: string, departmentData: UpdateDepartmentDTO): Promise<Department> {
    try {
      const response = await api.put(`/departments/${id}`, departmentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating department with id ${id}:`, error);
      throw error;
    }
  }

  // Delete department
  async deleteDepartment(id: string): Promise<boolean> {
    try {
      await api.delete(`/departments/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting department with id ${id}:`, error);
      throw error;
    }
  }

  // Activate department
  async activateDepartment(id: string): Promise<Department> {
    try {
      const response = await api.patch(`/departments/${id}/activate`);
      return response.data;
    } catch (error) {
      console.error(`Error activating department with id ${id}:`, error);
      throw error;
    }
  }

  // Deactivate department
  async deactivateDepartment(id: string): Promise<Department> {
    try {
      const response = await api.patch(`/departments/${id}/deactivate`);
      return response.data;
    } catch (error) {
      console.error(`Error deactivating department with id ${id}:`, error);
      throw error;
    }
  }

  // Search departments
  async searchDepartments(query: string): Promise<{ departments: Department[], total: number }> {
    try {
      const response = await api.get('/departments/search', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error(`Error searching departments with query "${query}":`, error);
      throw error;
    }
  }
}

export const departmentService = new DepartmentService();
export default departmentService; 